import React, { createContext, FC, MutableRefObject } from 'react';
import pathfinder from '@fiverr-private/pathfinder';
import { useLatest } from '@fiverr-private/hooks';
import { createUseContext } from '../../../utils';
import { DEFAULT_PORTFOLIO_MODAL_SERVICES_CONFIG, PortfolioModalServicesConfig } from '../../PortfolioModal';
import { PortfolioWidgetFullDataQueryUrlBuilder } from '../../../queries';
import { ProjectSourceFilter } from '../../../gql-operations/gql-generated';

export const buildRolesQueryParam = ({
  roleIds,
  roles,
  projectSource,
  subCategoryId,
}: {
  roleIds?: string[];
  roles?: PortfolioWidgetContextProviderProps['roles'];
  subCategoryId?: number;
  projectSource?: ProjectSourceFilter;
}) => ({
  roleIds: roleIds?.join(',') || '',
  ...(roles?.length && { 'roles[]': `${roles[0].roleId}|${roles[0].sellerRoleId}` }),
  ...(projectSource && { projectSource: projectSource || undefined }),
  ...(subCategoryId && { sc: subCategoryId.toString() }),
});

export interface PortfolioWidgetServicesConfig extends PortfolioModalServicesConfig {
  portfolioProjectsDataUrlBuilder: PortfolioWidgetFullDataQueryUrlBuilder;
}

export const DEFAULT_PORTFOLIO_WIDGET_SERVICES_CONFIG: PortfolioWidgetServicesConfig = {
  ...DEFAULT_PORTFOLIO_MODAL_SERVICES_CONFIG,
  portfolioProjectsDataUrlBuilder: ({ username, roleIds, roles, projectSource }) => {
    const query = buildRolesQueryParam({ roleIds, roles, projectSource });

    return pathfinder('portfolio_page_seller_portfolio_data', { username }, { query });
  },
};

export type BiEnrichment = Record<string, unknown>;

export interface Role {
  roleId: string;
  sellerRoleId: string;
}

export interface PortfolioWidgetContextValue {
  servicesConfig: PortfolioWidgetServicesConfig;
  username: string;
  roleIds?: string[];
  roles?: Role[];
  projectSource?: ProjectSourceFilter;
  subCategoryId?: number; // Used for listings query filtering
  queryClientDehydratedState?: unknown;
  biEnrichment: MutableRefObject<BiEnrichment | undefined>;
  inAttachmentReportRollout?: boolean;
}

export interface PortfolioWidgetContextProviderProps
  extends Omit<PortfolioWidgetContextValue, 'servicesConfig' | 'biEnrichment'> {
  servicesConfig?: Partial<PortfolioWidgetServicesConfig>;
  biEnrichment?: BiEnrichment;
}

const PortfolioWidgetContext = createContext<PortfolioWidgetContextValue | undefined>(undefined);

export const PortfolioWidgetContextProvider: FC<PortfolioWidgetContextProviderProps> = ({
  children,
  servicesConfig,
  username,
  roleIds,
  roles,
  subCategoryId,
  projectSource,
  queryClientDehydratedState,
  biEnrichment,
  inAttachmentReportRollout,
}) => {
  const latestBiEnrichment = useLatest(biEnrichment);

  return (
    <PortfolioWidgetContext.Provider
      value={{
        username,
        roleIds,
        roles,
        subCategoryId,
        projectSource,
        biEnrichment: latestBiEnrichment,
        servicesConfig: {
          ...DEFAULT_PORTFOLIO_WIDGET_SERVICES_CONFIG,
          ...servicesConfig,
        },
        queryClientDehydratedState,
        inAttachmentReportRollout,
      }}
    >
      {children}
    </PortfolioWidgetContext.Provider>
  );
};

export const usePortfolioWidgetContext = createUseContext(PortfolioWidgetContext);
